/**
 * @file Metadata-only registry for current parent-board modules.
 *
 * The registry intentionally has no imports. Renderers, state, routing,
 * controls, and mutation helpers may consume this metadata, but the registry
 * must not depend on those owners.
 */

export const BOARD_LAYOUTS = Object.freeze({
  board: "board",
  table: "table",
});

export const ACTIVE_BOARD_VIEW_KEYS = Object.freeze([
  "tasks",
  "patients",
  "labs",
  "projects",
  "assets",
  "maintenance",
]);

export const FUTURE_BOARD_VIEW_KEYS = Object.freeze([
]);

export const BOARD_VIEW_KEYS = ACTIVE_BOARD_VIEW_KEYS;

export const BOARD_CONTRACT_VIEW_KEYS = Object.freeze([
  ...ACTIVE_BOARD_VIEW_KEYS,
  ...FUTURE_BOARD_VIEW_KEYS,
]);

const DEFAULT_BOARD_VIEW_SETTINGS = Object.freeze({
  layout: BOARD_LAYOUTS.board,
  boardGroupBy: "date",
  tableGroupBy: "status",
  boardDateGroupingMode: "weekly",
  tableDateGroupingMode: "weekly",
  searchDraft: "",
  searchQuery: "",
  filters: Object.freeze({}),
  sortBy: null,
  sortDirection: "asc",
  columnWidths: Object.freeze({}),
});

export const BOARD_REGISTRY = Object.freeze({
  tasks: Object.freeze({
    viewKey: "tasks",
    itemType: "task",
    collectionName: "tasks",
    permissionKey: null,
    display: Object.freeze({
      singularLabel: "Task",
      pluralLabel: "Tasks",
    }),
    fields: Object.freeze({
      titleField: "title",
      statusField: "status",
      statusConfigKey: "taskStatus",
      categoryConfigKey: "taskCategoryConfig",
      typeConfigKey: "taskTypes",
      priorityConfigKey: "priorities",
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      boardDateGroupingMode: "daily",
      tableDateGroupingMode: "daily",
      visibleColumns: Object.freeze([
        "title",
        "parent",
        "types",
        "priority",
        "status",
        "owner",
        "assigned_users",
        "due_date",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: false,
      supportsTasks: false,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: false,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: false,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "task",
      tableAdapterKey: "task",
      detailAdapterKey: "task",
      formAdapterKey: "task",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "type",
        "urgency",
        "status",
        "assignee",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "priority", label: "Urgency" }),
        Object.freeze({ key: "status", label: "Status" }),
        Object.freeze({ key: "owner", label: "Owner" }),
        Object.freeze({ key: "assigned_users", label: "Assigned Users" }),
        Object.freeze({ key: "date", label: "Date" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "priority", label: "Urgency", kind: "multi" }),
        Object.freeze({ key: "status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "owner", label: "Owner", kind: "multi" }),
        Object.freeze({ key: "assigned_users", label: "Assigned Users", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
        Object.freeze({ key: "subtask", label: "Subtask", kind: "multi" }),
      ]),
    }),
  }),
  patients: Object.freeze({
    viewKey: "patients",
    itemType: "patient",
    collectionName: "patients",
    permissionKey: "patients",
    display: Object.freeze({
      singularLabel: "Patient",
      pluralLabel: "Patients",
    }),
    fields: Object.freeze({
      titleField: "name",
      statusField: "status",
      statusConfigKey: "patientStatus",
      categoryConfigKey: "patientCategoryConfig",
      typeConfigKey: "patientTypes",
      priorityConfigKey: "priorities",
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      visibleColumns: Object.freeze([
        "case_file_number",
        "name",
        "types",
        "priority",
        "status",
        "case_manager",
        "provider",
        "next_action_required",
        "next_appointment",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: false,
      supportsTasks: true,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: true,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: true,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "patient",
      tableAdapterKey: "patient",
      detailAdapterKey: "patient",
      formAdapterKey: "patient",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "type",
        "urgency",
        "status",
        "provider",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "priority", label: "Urgency" }),
        Object.freeze({ key: "status", label: "Status" }),
        Object.freeze({ key: "case_manager", label: "Case Manager" }),
        Object.freeze({ key: "provider", label: "Provider" }),
        Object.freeze({ key: "date", label: "Date" }),
        Object.freeze({ key: "case_value", label: "Case Value" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "priority", label: "Urgency", kind: "multi" }),
        Object.freeze({ key: "status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "case_manager", label: "Case Manager", kind: "multi" }),
        Object.freeze({ key: "provider", label: "Provider", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
      ]),
    }),
  }),
  projects: Object.freeze({
    viewKey: "projects",
    itemType: "project",
    collectionName: "projects",
    permissionKey: "projects",
    display: Object.freeze({
      singularLabel: "Project",
      pluralLabel: "Projects",
    }),
    fields: Object.freeze({
      titleField: "name",
      statusField: "status",
      statusConfigKey: "projectStatus",
      categoryConfigKey: "projectCategoryConfig",
      typeConfigKey: "projectTypes",
      priorityConfigKey: "priorities",
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      visibleColumns: Object.freeze([
        "name",
        "types",
        "priority",
        "status",
        "project_manager",
        "delivery_date",
        "next_due_date",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: true,
      supportsTasks: true,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: true,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: true,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "project",
      tableAdapterKey: "project",
      detailAdapterKey: "project",
      formAdapterKey: "project",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "type",
        "urgency",
        "status",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "priority", label: "Urgency" }),
        Object.freeze({ key: "status", label: "Status" }),
        Object.freeze({ key: "project_manager", label: "Project Manager" }),
        Object.freeze({ key: "date", label: "Date" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "priority", label: "Urgency", kind: "multi" }),
        Object.freeze({ key: "status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "project_manager", label: "Project Manager", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
      ]),
    }),
  }),
  assets: Object.freeze({
    viewKey: "assets",
    itemType: "asset",
    collectionName: "assets",
    permissionKey: "assets",
    display: Object.freeze({
      singularLabel: "Asset",
      pluralLabel: "Assets",
    }),
    fields: Object.freeze({
      titleField: "name",
      statusField: "equipment_status",
      statusConfigKey: "equipmentStatus",
      categoryConfigKey: "assetCategoryConfig",
      typeConfigKey: "assetTypes",
      priorityConfigKey: null,
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      visibleColumns: Object.freeze([
        "name",
        "serial_number",
        "types",
        "equipment_status",
        "next_service_due",
        "purchase_date",
        "supplier",
        "installer",
        "purchase_price",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: true,
      supportsTasks: true,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: true,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: true,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "asset",
      tableAdapterKey: "asset",
      detailAdapterKey: "asset",
      formAdapterKey: "asset",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "type",
        "status",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "equipment_status", label: "Status" }),
        Object.freeze({ key: "date", label: "Date" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "equipment_status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
      ]),
    }),
  }),
  labs: Object.freeze({
    viewKey: "labs",
    itemType: "lab",
    collectionName: "labs",
    permissionKey: "labs",
    display: Object.freeze({
      singularLabel: "Lab",
      pluralLabel: "Labs",
    }),
    fields: Object.freeze({
      titleField: "patient_name",
      statusField: "status",
      statusConfigKey: "labStatus",
      categoryConfigKey: "labCategoryConfig",
      typeConfigKey: "labTypes",
      priorityConfigKey: "priorities",
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      visibleColumns: Object.freeze([
        "patient_name",
        "lab",
        "types",
        "priority",
        "status",
        "provider",
        "assigned_users",
        "due_date",
        "cost",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: true,
      supportsTasks: true,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: true,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: true,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "lab",
      tableAdapterKey: "lab",
      detailAdapterKey: "lab",
      formAdapterKey: "lab",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "lab",
        "type",
        "urgency",
        "status",
        "assignee",
        "provider",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "lab", label: "Lab" }),
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "priority", label: "Urgency" }),
        Object.freeze({ key: "status", label: "Status" }),
        Object.freeze({ key: "assigned_users", label: "Assigned Users" }),
        Object.freeze({ key: "provider", label: "Provider" }),
        Object.freeze({ key: "date", label: "Date" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "lab", label: "Lab", kind: "multi" }),
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "priority", label: "Urgency", kind: "multi" }),
        Object.freeze({ key: "status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "assigned_users", label: "Assigned Users", kind: "multi" }),
        Object.freeze({ key: "provider", label: "Provider", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
      ]),
    }),
  }),
  maintenance: Object.freeze({
    viewKey: "maintenance",
    itemType: "maintenance",
    collectionName: "maintenance",
    permissionKey: "maintenance",
    display: Object.freeze({
      singularLabel: "Maintenance",
      pluralLabel: "Maintenance",
    }),
    fields: Object.freeze({
      titleField: "item",
      statusField: "status",
      statusConfigKey: "maintenanceStatus",
      categoryConfigKey: "maintenanceCategoryConfig",
      typeConfigKey: "maintenanceTypes",
      priorityConfigKey: "priorities",
    }),
    defaultViewSettings: Object.freeze({
      ...DEFAULT_BOARD_VIEW_SETTINGS,
      visibleColumns: Object.freeze([
        "item",
        "linked_to",
        "types",
        "priority",
        "status",
        "managed_by",
        "technician",
        "due_date",
        "completed_on",
        "cost",
      ]),
    }),
    capabilities: Object.freeze({
      supportsWip: true,
      supportsTasks: true,
      supportsTable: true,
      supportsKanban: true,
      supportsClosedPagination: true,
      supportsQuickAddTask: true,
    }),
    availability: Object.freeze({
      navigationEnabled: true,
      collectionListenerEnabled: true,
      detailRoutesEnabled: true,
    }),
    adapters: Object.freeze({
      cardAdapterKey: "maintenance",
      tableAdapterKey: "maintenance",
      detailAdapterKey: "maintenance",
      formAdapterKey: "maintenance",
    }),
    grouping: Object.freeze({
      supportedGroupKeys: Object.freeze([
        "type",
        "urgency",
        "status",
        "technician",
        "date",
      ]),
    }),
    runtimeControls: Object.freeze({
      sortOptions: Object.freeze([
        Object.freeze({ key: "stream", label: "Stream" }),
        Object.freeze({ key: "types", label: "Type" }),
        Object.freeze({ key: "priority", label: "Urgency" }),
        Object.freeze({ key: "status", label: "Status" }),
        Object.freeze({ key: "managed_by", label: "Managed By" }),
        Object.freeze({ key: "technician", label: "Technician" }),
        Object.freeze({ key: "date", label: "Date" }),
      ]),
      filterDefinitions: Object.freeze([
        Object.freeze({ key: "stream", label: "Stream", kind: "multi" }),
        Object.freeze({ key: "types", label: "Type", kind: "multi" }),
        Object.freeze({ key: "priority", label: "Urgency", kind: "multi" }),
        Object.freeze({ key: "status", label: "Status", kind: "multi" }),
        Object.freeze({ key: "managed_by", label: "Managed By", kind: "multi" }),
        Object.freeze({ key: "technician", label: "Technician", kind: "multi" }),
        Object.freeze({ key: "date", label: "Date", kind: "date" }),
      ]),
    }),
  }),
});

/**
 * Returns whether a view key belongs to the parent-board registry.
 * @param {string} viewKey - Candidate board view key.
 * @returns {boolean} True when the key is a registered parent-board view.
 */
export function isBoardViewKey(viewKey) {
  return BOARD_VIEW_KEYS.includes(viewKey);
}

/**
 * Returns whether a view key has a board-registry contract entry.
 * @param {string} viewKey - Candidate board view key.
 * @returns {boolean} True when the key has parent-board metadata.
 */
export function isRegisteredBoardViewKey(viewKey) {
  return BOARD_CONTRACT_VIEW_KEYS.includes(viewKey);
}

/**
 * Returns the parent-board metadata for a registered board view.
 * @param {string} viewKey - Registered parent-board view key.
 * @returns {object | null} Board metadata, or null for unsupported keys.
 */
export function getBoardRegistryEntry(viewKey) {
  return BOARD_REGISTRY[viewKey] || null;
}

/**
 * Returns all parent-board metadata entries.
 * @returns {Array<object>} Board metadata entries.
 */
export function getBoardRegistryEntries() {
  return BOARD_CONTRACT_VIEW_KEYS
    .map((viewKey) => getBoardRegistryEntry(viewKey))
    .filter(Boolean);
}

/**
 * Returns parent-board metadata by singular item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {object | null} Board metadata, or null for unsupported types.
 */
export function getBoardRegistryEntryByItemType(itemType) {
  return Object.values(BOARD_REGISTRY)
    .find((board) => board.itemType === itemType) || null;
}

/**
 * Returns the collection name for a registered parent-board item type.
 * @param {string} itemType - Singular parent-board item type.
 * @returns {string | null} Collection name, or null for unsupported types.
 */
export function getBoardCollectionNameForItemType(itemType) {
  const entry = getBoardRegistryEntryByItemType(itemType);
  return entry?.collectionName || null;
}

/**
 * Returns parent-board metadata by collection name.
 * @param {string} collectionName - Parent-board collection name.
 * @returns {object | null} Board metadata, or null for unsupported names.
 */
export function getBoardRegistryEntryByCollectionName(collectionName) {
  return getBoardRegistryEntries()
    .find((board) => board.collectionName === collectionName) || null;
}

/**
 * Returns the singular item type for a registered parent-board collection.
 * @param {string} collectionName - Parent-board collection name.
 * @returns {string | null} Singular item type, or null for unsupported names.
 */
export function getBoardItemTypeForCollectionName(collectionName) {
  const entry = getBoardRegistryEntryByCollectionName(collectionName);
  return entry?.itemType || null;
}

/**
 * Returns whether top-level navigation is enabled for a board.
 * @param {string} viewKey - Parent-board view key.
 * @returns {boolean} True when users may navigate to the board route.
 */
export function isBoardNavigationEnabled(viewKey) {
  return getBoardRegistryEntry(viewKey)?.availability?.navigationEnabled === true;
}

/**
 * Returns whether collection listeners are enabled for a board.
 * @param {string} viewKey - Parent-board view key.
 * @returns {boolean} True when bootstrap should attach collection listeners.
 */
export function isBoardCollectionListenerEnabled(viewKey) {
  return getBoardRegistryEntry(viewKey)
    ?.availability?.collectionListenerEnabled === true;
}

/**
 * Returns whether typed detail routes are enabled for a board.
 * @param {string} viewKey - Parent-board view key.
 * @returns {boolean} True when typed detail routes should parse.
 */
export function isBoardDetailRouteEnabled(viewKey) {
  return getBoardRegistryEntry(viewKey)?.availability?.detailRoutesEnabled === true;
}

function cloneDefaultBoardSettings(settings) {
  return {
    ...settings,
    filters: {},
    visibleColumns: [...settings.visibleColumns],
    columnWidths: {},
  };
}

/**
 * Builds mutable default view settings for the current parent boards.
 * @returns {Object<string, object>} Default settings keyed by board view.
 */
export function createDefaultBoardViewSettings() {
  return BOARD_CONTRACT_VIEW_KEYS.reduce((settings, viewKey) => {
    settings[viewKey] = cloneDefaultBoardSettings(
      BOARD_REGISTRY[viewKey].defaultViewSettings,
    );
    return settings;
  }, {});
}

/**
 * Returns the default visible table columns for a parent-board view.
 * @param {string} viewKey - Registered parent-board view key.
 * @returns {Array<string>} Mutable copy of default visible column keys.
 */
export function getDefaultBoardVisibleColumns(viewKey) {
  const defaultSettings = BOARD_REGISTRY[viewKey]?.defaultViewSettings;
  return defaultSettings ? [...defaultSettings.visibleColumns] : [];
}
